// LookStep.tsx — onboarding step 4 (spec D6, handoff §6 step "Look"). Color + welcome only
// (bot name / quick questions / position stay as whatever the bot already has — those are
// full Appearance-screen concerns, not onboarding's). Live preview reuses the same
// `WidgetPreview` component `BotAppearance.tsx` uses (extracted for exactly this reuse, M5
// task-5). "Continue" SAVES via the existing `PATCH /api/bots/:id { settings: { appearance
// } } ` — replace-per-top-level-key semantics, so the full appearance object (unedited
// fields spread from `initial`) is always sent, never a partial patch.
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { botSettingsSchema } from '@repo/validation';
import { Button } from '@repo/ui/components/button';
import { Label } from '@repo/ui/components/label';
import type { BotAppearanceSettings, BotDto } from '@repo/shared-types';
import { WidgetPreview } from '../WidgetPreview';
import { apiFetch } from '../../lib/api';
import { useT } from '../../locales/context';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export function LookStep({
  botId,
  bot,
  onNext,
}: {
  botId: string;
  bot: BotDto;
  onNext: () => void;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const initial = botSettingsSchema.parse(bot.settings ?? {}).appearance;

  const [color, setColor] = useState(initial.color);
  const [hexInput, setHexInput] = useState(initial.color);
  const [welcome, setWelcome] = useState(initial.welcome);
  const [error, setError] = useState<string | null>(null);

  function handleColorPicker(e: React.ChangeEvent<HTMLInputElement>) {
    setColor(e.target.value);
    setHexInput(e.target.value);
  }

  function handleHexBlur() {
    const trimmed = hexInput.trim();
    const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    if (HEX_RE.test(withHash)) {
      const normalized = withHash.toLowerCase();
      setColor(normalized);
      setHexInput(normalized);
    } else {
      setHexInput(color);
    }
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const appearance: BotAppearanceSettings = { ...initial, color, welcome };
      return apiFetch<BotDto>(`/api/bots/${botId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: { appearance } }),
      });
    },
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['bots', botId] });
      onNext();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="flex flex-col gap-5 rounded-[14px] border bg-card p-8 shadow-el-1">
      <div className="flex flex-col gap-1.5">
        <h1 className="font-display text-[26px] font-bold tracking-[-0.01em]">
          {t.onboarding.lookHeading}
        </h1>
        <p className="text-sm text-pretty text-muted-foreground">{t.onboarding.lookSubtitle}</p>
      </div>

      {/* Stacks until `lg`, not `sm`: between those breakpoints the second column was too
          narrow to render the widget at its real 380px, so the preview misreported the
          product's proportions. Below `lg` the preview drops beneath the form at full width,
          which is the same arrangement mobile already had and the one Johnny asked for. */}
      <div className="grid gap-5 lg:grid-cols-[1fr_380px] lg:items-start">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ob-color">{t.onboarding.brandColorLabel}</Label>
            <div className="flex items-center gap-2">
              <input
                id="ob-color"
                type="color"
                value={color}
                onChange={handleColorPicker}
                className="h-9 w-12 cursor-pointer rounded-md border border-input p-1"
              />
              <input
                aria-label={t.onboarding.colorHexAriaLabel}
                value={hexInput}
                onChange={(e) => setHexInput(e.target.value)}
                onBlur={handleHexBlur}
                maxLength={7}
                className="h-9 w-[110px] rounded-md border border-input bg-transparent px-3 font-mono text-[13px] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ob-welcome">{t.onboarding.welcomeMessageLabel}</Label>
            <textarea
              id="ob-welcome"
              rows={3}
              value={welcome}
              onChange={(e) => setWelcome(e.target.value)}
              maxLength={300}
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-[13.5px] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            />
          </div>
        </div>

        {/* 460px, not 280px, and capped in width when it stacks. The widget panel is
            `380px x min(600px, 80vh)` sitting ~88px off the bottom (`apps/widget/src/styles.ts`),
            so inside an iframe of height H an OPEN panel needs `0.8H + 88 <= H`, i.e. H >= 440.
            At 280 the panel overflowed the top by ~32px and was clipped — invisible at narrow
            widths only because the widget's own mobile media query makes it fill the frame, which
            hid the bug rather than avoiding it. The width cap keeps the stacked preview
            widget-shaped instead of a wide short letterbox. */}
        <div className="w-full max-w-[420px] lg:max-w-none">
          <WidgetPreview
            publicId={bot.publicId}
            color={color}
            botName={initial.botName}
            welcome={welcome}
            quickQuestions={initial.quickQuestions}
            height={460}
          />
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Button
        type="button"
        className="h-[42px] w-full"
        disabled={saveMutation.isPending}
        onClick={() => saveMutation.mutate()}
      >
        {saveMutation.isPending ? t.onboarding.lookSaving : t.onboarding.lookContinueCta}
      </Button>
    </div>
  );
}
